'use client';

/**
 * KPI Grid Component
 *
 * Displays key performance indicators with sparklines and deltas
 * Inspired by Stripe/Datadog dashboards
 *
 * @example
 * <KPIGrid
 *   items={[
 *     { title: 'Active Users', value: 1234, delta: 12, icon: Users },
 *     { title: 'Revenue', value: '$4,567', delta: 8, icon: DollarSign },
 *   ]}
 * />
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus, LucideIcon, Activity } from 'lucide-react';

export interface KPIItem {
  title: string;
  value: number | string;
  delta?: number;
  icon?: LucideIcon;
  iconColor?: string;
  inverseDelta?: boolean;
  subtitle?: string;
  sparklineData?: number[];
}

export interface KPIGridProps {
  items: KPIItem[];
  columns?: 2 | 3 | 4;
}

function formatNumber(num: number): string {
  if (typeof num !== 'number') return String(num);
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString();
}

function DeltaBadge({ value, inverse = false }: { value: number; inverse?: boolean }) {
  const isPositive = inverse ? value < 0 : value > 0;
  const isNeutral = value === 0;

  const Icon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;
  const colorClass = isNeutral
    ? 'text-white/50 bg-white/5'
    : isPositive
    ? 'text-green-400 bg-green-400/10'
    : 'text-red-400 bg-red-400/10';

  return (
    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      <Icon className="h-3 w-3" />
      <span>{Math.abs(value)}%</span>
    </div>
  );
}

function Sparkline({ data }: { data: number[] }) {
  if (!data || data.length === 0) {
    // Generate placeholder data
    data = [40, 65, 45, 80, 55, 90, 70, 85, 60, 75, 95, 80];
  }

  const max = Math.max(...data);

  return (
    <div className="mt-4 h-8 flex items-end gap-0.5">
      {data.map((value, i) => (
        <div
          key={i}
          className="flex-1 bg-white/10 rounded-sm transition-all hover:bg-white/20"
          style={{ height: `${(value / max) * 100}%` }}
        />
      ))}
    </div>
  );
}

function KPICard({ item }: { item: KPIItem }) {
  const Icon = item.icon || Activity;
  const iconColor = item.iconColor || 'bg-violet-500/20 text-violet-400';

  return (
    <div className="bg-slate-900/60 backdrop-blur-lg border border-white/10 rounded-xl p-5 hover:border-white/20 transition-all hover:scale-[1.02]">
      <div className="flex items-start justify-between mb-4">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${iconColor}`}>
          <Icon className="h-5 w-5" />
        </div>
        {item.delta !== undefined && (
          <DeltaBadge value={item.delta} inverse={item.inverseDelta} />
        )}
      </div>

      <div className="space-y-1">
        <div className="text-3xl font-bold text-white tracking-tight">
          {typeof item.value === 'number' ? formatNumber(item.value) : item.value}
        </div>
        <div className="text-sm font-medium text-white/60">
          {item.title}
        </div>
        {item.subtitle && (
          <div className="text-xs text-white/40">{item.subtitle}</div>
        )}
      </div>

      <Sparkline data={item.sparklineData || []} />
      <div className="text-xs text-white/40 mt-2">Last 24 hours</div>
    </div>
  );
}

export function KPIGrid({ items, columns = 4 }: KPIGridProps) {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid gap-4 ${gridCols[columns]}`}>
      {items.map((item, index) => (
        <KPICard key={item.title + index} item={item} />
      ))}
    </div>
  );
}
